import React from "react";
import { BlogPost } from "@/types/blog";
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Skeleton } from "@/components/ui/skeleton";
import { Plus, BarChart2, Clock, UserCheck, Tag } from "lucide-react";

interface BlogOverviewProps {
  posts: BlogPost[];
  loading: boolean;
  onCreatePost: () => void;
}

const metricCardClass = "rounded-2xl border border-slate-200 bg-white shadow-sm";

function MetricSkeleton() {
  return (
    <Card className={metricCardClass}>
      <CardHeader>
        <Skeleton className="h-4 w-24" />
      </CardHeader>
      <CardContent className="space-y-2">
        <Skeleton className="h-8 w-20" />
        <Skeleton className="h-3 w-32" />
      </CardContent>
    </Card>
  );
}

const BlogOverview: React.FC<BlogOverviewProps> = ({ posts, loading, onCreatePost }) => {
  const totalPosts = posts.length;
  const publishedPosts = posts.filter(post => post.published || post.status === "published").length;
  const draftPosts = totalPosts - publishedPosts;
  const lastPublished = posts.find(post => post.published || post.status === "published");
  const uniqueAuthors = Array.from(new Set(posts.map(post => post.author || post.author_email).filter(Boolean)));

  const categoriesCount = posts.reduce<Record<string, number>>((acc, post) => {
    const category = post.category || "Sem categoria";
    acc[category] = (acc[category] || 0) + 1;
    return acc;
  }, {});

  const topCategories = Object.entries(categoriesCount)
    .sort((a, b) => b[1] - a[1])
    .slice(0, 3);

  return (
    <div className="space-y-6">
      <div className="flex flex-col gap-4 md:flex-row md:items-center md:justify-between">
        <div>
          <h2 className="text-2xl font-semibold text-slate-900">Visão geral do blog</h2>
          <p className="text-sm text-slate-600">Acompanhe desempenho, autores e categorias em destaque.</p>
        </div>
        <Button className="bg-[#FF5605] hover:bg-[#e34b00]" onClick={onCreatePost}>
          <Plus className="mr-2 h-4 w-4" />
          Novo post
        </Button>
      </div>

      <div className="grid gap-4 md:grid-cols-2 xl:grid-cols-4">
        {loading ? (
          Array.from({ length: 4 }).map((_, index) => <MetricSkeleton key={index} />)
        ) : (
          <>
            <Card className={metricCardClass}>
              <CardHeader className="flex flex-row items-center justify-between space-y-0 pb-2">
                <CardTitle className="text-sm font-medium text-slate-600">Posts publicados</CardTitle>
                <BarChart2 className="h-4 w-4 text-[#FF5605]" />
              </CardHeader>
              <CardContent>
                <div className="text-3xl font-semibold text-slate-900">{publishedPosts}</div>
                <p className="text-xs text-slate-500">{totalPosts} posts no total</p>
              </CardContent>
            </Card>

            <Card className={metricCardClass}>
              <CardHeader className="flex flex-row items-center justify-between space-y-0 pb-2">
                <CardTitle className="text-sm font-medium text-slate-600">Rascunhos</CardTitle>
                <Clock className="h-4 w-4 text-[#FF5605]" />
              </CardHeader>
              <CardContent>
                <div className="text-3xl font-semibold text-slate-900">{draftPosts}</div>
                <p className="text-xs text-slate-500">Prontos para revisão e publicação</p>
              </CardContent>
            </Card>

            <Card className={metricCardClass}>
              <CardHeader className="flex flex-row items-center justify-between space-y-0 pb-2">
                <CardTitle className="text-sm font-medium text-slate-600">Autores ativos</CardTitle>
                <UserCheck className="h-4 w-4 text-[#FF5605]" />
              </CardHeader>
              <CardContent>
                <div className="text-3xl font-semibold text-slate-900">{uniqueAuthors.length}</div>
                <p className="text-xs text-slate-500">Equipe com acesso ao blog</p>
              </CardContent>
            </Card>

            <Card className={metricCardClass}>
              <CardHeader className="flex flex-row items-center justify-between space-y-0 pb-2">
                <CardTitle className="text-sm font-medium text-slate-600">Último post publicado</CardTitle>
                <BarChart2 className="h-4 w-4 text-[#FF5605]" />
              </CardHeader>
              <CardContent>
                <div className="text-lg font-medium text-slate-900 leading-tight">
                  {lastPublished?.title ?? "Ainda não há posts publicados"}
                </div>
                {lastPublished?.updated_at && (
                  <p className="text-xs text-slate-500 mt-2">
                    Atualizado em {new Date(lastPublished.updated_at).toLocaleDateString("pt-BR")}
                  </p>
                )}
              </CardContent>
            </Card>
          </>
        )}
      </div>

      <Card className="rounded-2xl border border-slate-200 bg-white shadow-sm">
        <CardHeader>
          <CardTitle className="flex items-center text-sm font-medium text-slate-600">
            <Tag className="mr-2 h-4 w-4 text-[#FF5605]" /> Categorias em destaque
          </CardTitle>
        </CardHeader>
        <CardContent>
          {loading ? (
            <div className="space-y-2">
              <Skeleton className="h-3 w-40" />
              <Skeleton className="h-3 w-32" />
              <Skeleton className="h-3 w-24" />
            </div>
          ) : topCategories.length > 0 ? (
            <ul className="space-y-3">
              {topCategories.map(([category, count]) => (
                <li key={category} className="flex items-center justify-between rounded-lg border border-slate-100 bg-slate-50 px-4 py-3">
                  <span className="text-sm font-medium text-slate-700">{category}</span>
                  <span className="text-xs font-semibold text-[#FF5605]">{count} posts</span>
                </li>
              ))}
            </ul>
          ) : (
            <p className="text-sm text-slate-500">Nenhuma categoria cadastrada ainda.</p>
          )}
        </CardContent>
      </Card>
    </div>
  );
};

export default BlogOverview;
